import React, { Component } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import {
  Table,
  TableBody,
  TableHeaderColumn,
  TableRow,
} from 'material-ui/Table';
import { grey500 } from 'material-ui/styles/colors';
import CircularProgress from 'material-ui/CircularProgress';
import CommentsWidgetListItem from './components/CommentsWidgetListItem';
import withQueryStringProps from '../../components/withQueryStringProps';
import { QUESTION_TYPE_SMILEYCOMMENT } from '../../constants';

@withQueryStringProps(['print'])
class CommentsWidgetList extends Component {

  componentDidUpdate() {
    if (this.props.scrolledToBottom && this.props.relay.hasMore() && !this.props.relay.isLoading()) {
      this.props.relay.loadMore(
        100,
        err => {
          if (err) {
            console.log(err);
          }
        },
      );
    }
  }

  render() {
    const question = this.props.question || {};
    const answerEdges = (question.answers && question.answers.edges) || [];
    const answerNodes = answerEdges.map(edge => edge.node);

    let timeWidth;
    if (this.props.showTimeColumn) {
      timeWidth = 165;
    } else {
      timeWidth = 0;
    }

    let smileyCommentWidth;
    if (question.type === QUESTION_TYPE_SMILEYCOMMENT) {
      smileyCommentWidth = 60;
    } else {
      smileyCommentWidth = 0;
    }

    let deviceWidth;
    if (this.props.showTerminalColumn) {
      if (this.props.width <= (timeWidth + 400)) {
        deviceWidth = (this.props.width - timeWidth - smileyCommentWidth) * 0.4;
      } else {
        deviceWidth = (this.props.width - timeWidth - smileyCommentWidth) * 0.3;
      }
    } else {
      deviceWidth = 0;
    }

    let extraColumnWidth;
    let extraColumnWidths;
    if (this.props.extraColumns && this.props.extraColumns.length > 0) {
      let numberOfFlexColumns = this.props.extraColumns.length;
      if (this.props.showTerminalColumn) {
        numberOfFlexColumns++;
      }
      const availableWidth = (this.props.width - timeWidth - smileyCommentWidth) * 0.6;
      const width = availableWidth * (1 / numberOfFlexColumns);
      if (this.props.showTerminalColumn) {
        deviceWidth = width;
      }
      extraColumnWidth = width;
      extraColumnWidths = width * this.props.extraColumns.length;
    } else {
      extraColumnWidth = 0;
      extraColumnWidths = 0;
    }

    const commentWidth = this.props.width - timeWidth - deviceWidth - extraColumnWidths;

    const columnWidths = {
      time: timeWidth,
      device: deviceWidth,
      extraColumn: extraColumnWidth,
      smileyComment: smileyCommentWidth,
      comment: commentWidth,
    };

    const styles = {
      headerCell: {
        boxSizing: 'border-box',
        height: 'auto',
        paddingTop: 15,
        paddingBottom: 15,
        paddingLeft: 5,
        paddingRight: 5,
      },
      timeHeaderCell: {
        width: columnWidths.time,
      },
      deviceHeaderCell: {
        width: columnWidths.device,
      },
      extraColumnHeaderCell: {
        width: columnWidths.extraColumn,
      },
      smileyCommentHeaderCell: {
        width: columnWidths.smileyComment,
      },
      commentHeaderCell: {
        width: columnWidths.comment,
      },
      loadingWrapper: {
        padding: '20px 0',
        textAlign: 'center',
      },
      printMessageWrapper: {
        color: grey500,
        padding: '40px 0 50px',
        textAlign: 'center',
      },
      printMessage: {
        width: 620,
        margin: '0 auto',
      },
    };

    return (
      <div>
        <Table selectable={false}>
          <TableBody displayRowCheckbox={false}>
            <TableRow>
              {this.props.showTimeColumn && (
                <TableHeaderColumn style={Object.assign({}, styles.headerCell, styles.timeHeaderCell)}>
                  <FormattedMessage id="time" />
                </TableHeaderColumn>
              )}
              {this.props.showTerminalColumn && (
                <TableHeaderColumn style={Object.assign({}, styles.headerCell, styles.deviceHeaderCell)}>
                  <FormattedMessage id="terminal" />
                </TableHeaderColumn>
              )}
              {this.props.extraColumns && this.props.extraColumns.map(column =>
                <TableHeaderColumn
                  key={column.questionId}
                  style={Object.assign({}, styles.headerCell, styles.extraColumnHeaderCell)}
                >
                  {column.title}
                </TableHeaderColumn>
              )}
              {question.type === QUESTION_TYPE_SMILEYCOMMENT && (
                <TableHeaderColumn
                  style={Object.assign({}, styles.headerCell, styles.smileyCommentHeaderCell)}
                >
                  <FormattedMessage id="answer" />
                </TableHeaderColumn>
              )}
              <TableHeaderColumn style={Object.assign({}, styles.headerCell, styles.commentHeaderCell)}>
                <FormattedMessage id="comment" />
              </TableHeaderColumn>
            </TableRow>
            {answerNodes.map(answer =>
              <CommentsWidgetListItem
                key={answer.id}
                answer={answer}
                columnWidths={columnWidths}
                showTerminalColumn={this.props.showTerminalColumn}
                showTimeColumn={this.props.showTimeColumn}
                showLocation={this.props.showLocation}
                showSmileyComment={question.type === QUESTION_TYPE_SMILEYCOMMENT}
                extraColumns={this.props.extraColumns}
              />
            )}
          </TableBody>
        </Table>
        {this.props.relay.hasMore() && !this.props.print && (
          <div style={styles.loadingWrapper}>
            <CircularProgress
              size={50}
              thickness={3}
            />
          </div>
        )}
        {this.props.relay.hasMore() && this.props.print && (
          <div style={styles.printMessageWrapper}>
            <div style={styles.printMessage}>
              <FormattedMessage id="moreCommentsAvailable" />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default createPaginationContainer(
  CommentsWidgetList,
  {
    question: graphql`
      fragment CommentsWidgetList_question on Question {
        id
        type
        answers(
          from: $from
          to: $to
          deviceIds: $deviceIds
          filterComments: $filterComments
          demoData: $demoData
          questionFilters: $questionFilters
          lookupAnswersToQuestions: $commentExtraColumns
          first: $count
          after: $cursor
        ) @connection(key: "CommentsWidgetList_answers") {
          edges {
            node {
              id
              ...CommentsWidgetListItem_answer
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.question && props.question.answers;
    },
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        questionId: props.question.id,
        from: fragmentVariables.from,
        to: fragmentVariables.to,
        deviceIds: fragmentVariables.deviceIds,
        filterComments: fragmentVariables.filterComments,
        demoData: fragmentVariables.demoData,
        questionFilters: fragmentVariables.questionFilters,
        count,
        cursor,
      }
    },
    query: graphql`
      query CommentsWidgetListPaginationQuery(
        $questionId: ID!
        $from: String!
        $to: String!
        $deviceIds: [ID]!
        $filterComments: Boolean!
        $demoData: Boolean!
        $questionFilters: [QuestionFilterInput]
        $commentExtraColumns: [ID]
        $count: Int!
        $cursor: String
      ) {
        question(id: $questionId) {
          ...CommentsWidgetList_question
        }
      }
    `,
  },
);



// WEBPACK FOOTER //
// ./src/components/CommentsWidgetList/CommentsWidgetList.js